import { useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Upload, X, User } from 'lucide-react';
import { expertProfileUploadApi } from '../services/expertProfileUploadApi';
import ExpertAvatarCropper from './ExpertAvatarCropper';
import {
  getFileTypeError,
  EXPERT_AVATAR_OUTPUT,
  getCroppedExpertAvatarImage,
  compressExpertAvatarIfNeeded,
  uploadToS3WithRetry,
} from '../utils/expertProfileImageUtils';

interface UploadedAvatar {
  documentId: number;
  viewUrl: string;
  fileType: string;
}

interface ExpertAvatarUploadProps {
  value: UploadedAvatar | null;
  onChange: (avatar: UploadedAvatar | null) => void;
}

export default function ExpertAvatarUpload({ value, onChange }: ExpertAvatarUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const error = getFileTypeError(file);
    if (error) {
      toast.error(error);
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result as string);
    reader.readAsDataURL(file);
  }, []);

  const handleCropComplete = useCallback(
    async (croppedAreaPixels: { x: number; y: number; width: number; height: number }) => {
      if (!imageSrc || !selectedFile) return;

      try {
        setIsUploading(true);

        const croppedBlob = await getCroppedExpertAvatarImage(imageSrc, croppedAreaPixels);
        const finalBlob = await compressExpertAvatarIfNeeded(croppedBlob);
        const document = await expertProfileUploadApi.createDocument({
          fileType: EXPERT_AVATAR_OUTPUT.fileType,
          mime: EXPERT_AVATAR_OUTPUT.mime,
        });

        await uploadToS3WithRetry(document.uploadUrl, finalBlob);

        await expertProfileUploadApi.markAsUploaded({
          documentId: parseInt(document.id, 10),
        });

        onChange({
          documentId: parseInt(document.id, 10),
          viewUrl: document.viewUrl,
          fileType: document.fileType,
        });

        setImageSrc(null);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';

        toast.success('Avatar uploaded successfully');
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(error instanceof Error ? error.message : 'Failed to upload avatar');
      } finally {
        setIsUploading(false);
      }
    },
    [imageSrc, selectedFile, onChange]
  );

  const handleCropCancel = useCallback(() => {
    setImageSrc(null);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleRemove = useCallback(() => {
    onChange(null);
  }, [onChange]);

  return (
    <div className="space-y-3">
      {/* Current avatar preview or upload prompt */}
      {value?.viewUrl ? (
        <div className="flex items-center gap-4">
          <img
            src={value.viewUrl}
            alt="Expert avatar"
            className="w-20 h-20 rounded-full object-cover border-2 border-pine-line"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-1.5 text-sm rounded-lg border border-pine-line text-pine-ink-soft hover:bg-pine-hover-button transition-colors"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="px-3 py-1.5 text-sm rounded-lg border border-red-500/20 text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="flex items-center gap-3 p-4 rounded-lg border-2 border-dashed border-pine-line hover:border-pine-pine/50 transition-colors w-full"
        >
          <div className="w-16 h-16 rounded-full bg-pine-paper flex items-center justify-center">
            <User className="w-8 h-8 text-pine-ink-soft" />
          </div>
          <div className="text-left">
            <p className="text-sm font-medium text-pine-ink">
              {isUploading ? 'Uploading...' : 'Upload Avatar'}
            </p>
            <p className="text-xs text-pine-ink-soft">
              160 x 160px, JPEG/PNG/WebP, max 500KB
            </p>
          </div>
          <Upload className="w-5 h-5 text-pine-ink-soft ml-auto" />
        </button>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Cropper modal */}
      {imageSrc && (
        <ExpertAvatarCropper
          imageSrc={imageSrc}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
}
